import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { useInstanceId, useUpdateSettings } from '@/features/admin/queries'
import { settingsQuery } from '@/features/settings/queries'
import { CopyButton } from '@/shared/components/copy-button'
import { Skeleton } from '@/shared/ui/skeleton'
import { Switch } from '@/shared/ui/switch'

/**
 * The settings that belong to the instance: both of the ones the endpoint
 * carries.
 */
export function AdminSettingsPage() {
  const { t } = useTranslation()
  const settings = useQuery(settingsQuery())
  const instance = useInstanceId()
  const { update } = useUpdateSettings()

  if (settings.isPending) return <Skeleton className="m-6 h-64 max-w-2xl" />

  const held = settings.data

  return (
    <div className="mx-auto w-full max-w-2xl space-y-8 p-6">
      <header>
        <h1 className="text-xl font-semibold">{t('admin.settingsTitle')}</h1>
      </header>

      <section className="space-y-4 rounded-lg border p-4 bg-background on-surface">
        <h2 className="font-medium">{t('admin.instance')}</h2>
        <Row name={t('admin.instanceId')} description={t('admin.instanceIdWhy')}>
          {instance.data === undefined ? (
            <Skeleton className="h-8 w-40" />
          ) : (
            <div className="flex items-center gap-2">
              <code className="truncate text-sm">{instance.data.instance_id}</code>
              <CopyButton
                value={instance.data.instance_id}
                label={t('admin.copyInstanceId')}
              />
            </div>
          )}
        </Row>
        <Row
          name={t('admin.verifyImportSignature')}
          description={t('admin.verifyImportSignatureWhy')}
        >
          <Setting
            id="verify-import-signature"
            label={t('admin.verifyImportSignature')}
            checked={held?.verify_import_signature ?? false}
            onChange={(value) => {
              update({ verify_import_signature: value })
            }}
          />
        </Row>
      </section>

      <section className="space-y-4 rounded-lg border p-4 bg-background on-surface">
        <h2 className="font-medium">{t('admin.maintenance')}</h2>
        <Row
          name={t('admin.trackWorkspaceUsage')}
          description={t('admin.trackWorkspaceUsageWhy')}
        >
          <Setting
            id="track-workspace-usage"
            label={t('admin.trackWorkspaceUsage')}
            checked={held?.track_workspace_usage ?? false}
            onChange={(value) => {
              update({ track_workspace_usage: value })
            }}
          />
        </Row>
      </section>
    </div>
  )
}

function Row({
  name,
  description,
  children,
}: {
  name: string
  description: string
  children: React.ReactNode
}) {
  return (
    <div className="flex items-start justify-between gap-6 border-t pt-4 first-of-type:border-t-0 first-of-type:pt-0">
      <div className="min-w-0">
        <p className="text-sm font-medium">{name}</p>
        <p className="text-muted-foreground text-sm">{description}</p>
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  )
}

/**
 * A switch that saves the moment it is flipped, since there is nothing else to
 * say about it.
 *
 * Named after the setting rather than labelled "Enabled". Upstream writes the
 * word beside every one of them, which leaves a screen reader announcing three
 * switches called "Enabled" on one page -- and the word says nothing the
 * switch's own state does not.
 */
function Setting({
  id,
  label,
  checked,
  onChange,
}: {
  id: string
  label: string
  checked: boolean
  onChange: (value: boolean) => void
}) {
  return (
    <Switch id={id} aria-label={label} checked={checked} onCheckedChange={onChange} />
  )
}
